import { useRef, useState, useMemo, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button, ChevronLeft, ChevronRight, Icon } from '@mesh.ai/rabbit';
import { Coin } from 'interfaces/MeshMaven';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import useHelpMeWriteTooltip from 'screens/common/useHelpMeWriteTooltip';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import styled from 'styled-components';

import { CheckCircle, CloseIcon } from 'assets/assetsV1/iconComponents';
import { ContentAnalyzerGradient } from 'assets/assetsV1/iconComponents/ContentAnalyzerGradient';

import { getQueryParams } from 'constants/helper';
import { sendReanalyzedMeshMavenContentEvent } from 'constants/mixpanelHelper';
import { CF_GEN_AI_BUTTON_TYPES, sendCFGenAIButtonClickedEvent } from 'constants/mixpanelHelpers/cfGenAIEvents';

import { Divider } from 'uiToolkitV1/Divider';
import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { MESH_MAVEN_DEI_CONSTANTS } from '../../constants';
import {
  useFeedbackAnalysisLoading,
  useFeedbackAnalysisMutation,
  useRephraseTextLoading,
} from '../../hooks/meshMavenDataHooks';
import { useFeedbackData, usePrimaryPanelAction } from '../../slice/feedbackSlice';
import MeshMavenError from './MeshMavenError';
import MeshMavenLoading from './MeshMavenLoading';

const COIN_FEEDBACK_TEXT: Record<keyof Coin, string> = {
  context: 'Context: what project, goal, task or situation you are giving feedback on.',
  observations: 'Observation: what action or behavior that you have observed',
  impact: 'Impact: the outcome of the action or behavior (whether positive or negative).',
  next_steps: 'Next Steps: what you would suggest are the things that should be done to improve themselves.',
};

const StyledSlider = styled(Slider)`
  .slick-list > div {
    margin-left: 0;
  }

  .slick-list {
    margin-left: -10px;
  }

  // display flex and height inherit of .slick-slide is to make child height 100%.
  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    height: inherit;
    flex: ${(props) => (props.stretchChildren ? 1 : 'unset')};
    margin-left: 10px;

    // making heights of div 100% so that children can attain 100% height.
    & > div {
      height: 100%;

      // setting children height to 100% - 1px in case they have shadows.
      > * {
        // height: calc(100% - 1px);
        display: ${(props) => (props.stretchChildren ? 'flex !important' : 'test-block')};

        // to remove the focus outline
        &:focus {
          outline: none;
        }
      }
    }
  }
`;

const FeedbackAnalysis = () => {
  const { analysisError, selectedRevision } = useFeedbackData();

  const feedbackAnalysis = selectedRevision.feedbackAnalysis ?? {};

  const isAnalyzeFetching = useFeedbackAnalysisLoading();
  const isRephraseFetching = useRephraseTextLoading();

  if (isAnalyzeFetching || isRephraseFetching) {
    return <MeshMavenLoading loadingText="Analyzing the comment via Maven" />;
  }

  if (analysisError) {
    return <MeshMavenError errorText={analysisError} />;
  }

  const positiveAnalysis = Object.keys(feedbackAnalysis).filter((k) => feedbackAnalysis[k] === 1);

  const negativeAnalysis = Object.keys(feedbackAnalysis).filter((k) => feedbackAnalysis[k] === 0);

  return (
    <Flex flexDir="column" py={3} px={5}>
      {selectedRevision.isReanalyzeEnabled && (
        <TextLabel bold color="alerts.in_progress" mb={2}>
          Text changes detected, click on "Reanalyse" for updated analysis.
        </TextLabel>
      )}
      <TextBodyMedium mb={3}>Maven analysed your text and here&apos;s what it found:</TextBodyMedium>
      {positiveAnalysis.length > 0 && (
        <>
          <TextLabel bold mb={1}>
            Your comment includes:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {positiveAnalysis.map((k) => (
              <Flex alignItems="flex-start" gap={2}>
                <CheckCircle mt="3px" color="functional.2F8133" />
                <TextBodyMedium>{COIN_FEEDBACK_TEXT[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
      {negativeAnalysis.length > 0 && (
        <>
          <TextLabel bold mt={3} mb={1}>
            Your comment could improve by including:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {negativeAnalysis.map((k) => (
              <Flex alignItems="flex-start" gap={2}>
                <CloseIcon mt="3px" color="primary.FD812D" />
                <TextBodyMedium>{COIN_FEEDBACK_TEXT[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </Flex>
  );
};

const DiversityAndInclusionAnalysis = ({
  deiAnalysis,
}: {
  deiAnalysis: { issue: string; suggestions: string[]; word: string }[];
}) => {
  const isAnalyzeFetching = useFeedbackAnalysisLoading();
  const isRephraseFetching = useRephraseTextLoading();

  if (deiAnalysis.length === 0 || isAnalyzeFetching || isRephraseFetching) {
    return null;
  }

  return (
    <Box>
      <Flex p="10px 20px 6px" alignItems="center" w="100%" justifyContent="space-between">
        <TextBodyMedium bold w="100%">
          Diversity and inclusion analysis
        </TextBodyMedium>{' '}
        <Box h="34px" w="0px" />
      </Flex>
      <Divider color="neutral.EBEFF3" m={1} />
      <Box p="12px 16px">
        <TextBodyMedium>We found some phrases that need rework</TextBodyMedium>
        <Box py={3} px={5}>
          <ul
            style={{
              padding: 0,
              margin: 0,
            }}
          >
            {deiAnalysis.map(({ issue, suggestions, word }) => (
              <li style={{ marginBottom: '4px' }}>
                <TextBodyMedium>
                  "{word}" - {issue} Try replacing with "{suggestions.join('", "')}"
                </TextBodyMedium>
              </li>
            ))}
          </ul>
        </Box>{' '}
      </Box>
    </Box>
  );
};

const MeshMavenAnalyzeAndDEI = () => {
  const parentRef = useRef(null);
  const sliderRef = useRef(null);

  const [activeSlide, setActiveSlide] = useState(0);

  const { mutate: getFeedbackAnalysis } = useFeedbackAnalysisMutation();

  const isRephraseFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();

  const { revisionNumber, selectedRevision } = useFeedbackData();

  const history = useHistory();
  const { source } = getQueryParams(history.location.search);
  const action = usePrimaryPanelAction();
  const helpMeWriteTooltip = useHelpMeWriteTooltip({
    editorState: selectedRevision.updatedState,
    checkMention: action === 'SHARE',
  });

  const handleReanalyze = () => {
    sendCFGenAIButtonClickedEvent({
      eventProperties: {
        source,
        type: CF_GEN_AI_BUTTON_TYPES.REANALYZE,
      },
    });
    sendReanalyzedMeshMavenContentEvent({
      eventProperties: {
        source: 'Share Feedback',
      },
    });
    getFeedbackAnalysis({
      revision: selectedRevision,
      revisionNumber,
      editorState: selectedRevision.updatedState,
    });
  };

  // DEI stuff

  const inputText = selectedRevision.updatedState.getCurrentContent().getPlainText();

  const keysToSearch = Object.keys(MESH_MAVEN_DEI_CONSTANTS);

  const deiAnalysis = keysToSearch.reduce(
    (acc, key) => {
      const { issue, suggestion: suggestions } = MESH_MAVEN_DEI_CONSTANTS[key];

      if (inputText.toLowerCase().includes(key.toLowerCase())) {
        acc.push({ issue, suggestions, word: key });
      }

      return acc;
    },
    [] as { issue: string; suggestions: string[]; word: string }[]
  );

  const hasDEIAnalysis = useMemo(() => deiAnalysis?.length > 0, [deiAnalysis]);

  const goToNextSlide = () => {
    sliderRef.current.slickNext();
    setActiveSlide((prev) => prev + 1);
  };

  const goToPrevSlide = () => {
    sliderRef.current.slickPrev();
    setActiveSlide((prev) => prev - 1);
  };

  useEffect(() => {
    if (activeSlide === 1 && !hasDEIAnalysis) {
      setActiveSlide(0);
      sliderRef.current.slickGoTo(0);
    }
  }, [activeSlide, hasDEIAnalysis]);

  if (!selectedRevision) {
    return null;
  }

  return (
    <>
      <StyledSlider
        ref={sliderRef}
        slidesToShow={hasDEIAnalysis ? 1.1 : 1}
        slidesToScroll={1}
        draggable={false}
        infinite={false}
      >
        <Flex
          height="100%"
          ref={parentRef}
          flexDir="column"
          borderRadius="8px"
          borderWidth="1px"
          borderColor="neutral.EBEFF3"
        >
          <Flex p="10px 20px 6px" alignItems="center" w="100%" justifyContent="space-between">
            <TextBodyMedium bold w="100%">
              Feedback comment analysis
            </TextBodyMedium>
            <Tooltip placement="bottom-end" label={helpMeWriteTooltip.tooltip}>
              <Box
                sx={{
                  button: {
                    marginLeft: 'auto',
                  },
                }}
              >
                <GradientButton
                  // @ts-expect-error text prop is missing
                  text="Reanalyse"
                  buttonProps={{
                    StartIcon: ContentAnalyzerGradient,
                  }}
                  onClick={handleReanalyze}
                  isDisabled={
                    !selectedRevision.isReanalyzeEnabled ||
                    isRephraseFetching ||
                    isAnalysisFetching ||
                    helpMeWriteTooltip.isDisabled
                  }
                />
              </Box>
            </Tooltip>
          </Flex>
          <Divider color="neutral.EBEFF3" m={1} />
          <FeedbackAnalysis />
        </Flex>
        <Flex height="100%" flexDir="column" borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
          <DiversityAndInclusionAnalysis deiAnalysis={deiAnalysis} />
        </Flex>
      </StyledSlider>
      {hasDEIAnalysis && (
        <Flex
          sx={{
            // targeting disabled button
            'button[disabled]': {
              color: '#99D2F9',
            },
          }}
          justifyContent="flex-end"
        >
          <Button variant="text" onClick={goToPrevSlide} disabled={activeSlide === 0}>
            <Icon IconSVG={ChevronLeft} />
          </Button>
          <Button variant="text" onClick={goToNextSlide} disabled={activeSlide === 1}>
            <Icon IconSVG={ChevronRight} />
          </Button>
        </Flex>
      )}
    </>
  );
};

const MeshMavenAnalyze = () => {
  const parentRef = useRef(null);

  const { mutate: getFeedbackAnalysis } = useFeedbackAnalysisMutation();

  const isRephraseFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();

  const { revisionNumber, selectedRevision } = useFeedbackData();

  const history = useHistory();
  const { source } = getQueryParams(history.location.search);
  const action = usePrimaryPanelAction();
  const helpMeWriteTooltip = useHelpMeWriteTooltip({
    editorState: selectedRevision.updatedState,
    checkMention: action === 'SHARE',
  });

  if (!selectedRevision) {
    return null;
  }

  const handleReanalyze = () => {
    sendCFGenAIButtonClickedEvent({
      eventProperties: {
        source,
        type: CF_GEN_AI_BUTTON_TYPES.REANALYZE,
      },
    });
    sendReanalyzedMeshMavenContentEvent({
      eventProperties: {
        source: 'Share Feedback',
      },
    });
    getFeedbackAnalysis({
      revision: selectedRevision,
      revisionNumber,
      editorState: selectedRevision.updatedState,
    });
  };

  return (
    <Flex ref={parentRef} flexDir="column" borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
      <Flex p="10px 20px 6px" alignItems="center" w="100%" justifyContent="space-between">
        <TextBodyMedium bold w="100%">
          Feedback comment analysis
        </TextBodyMedium>
        <Tooltip placement="bottom-end" label={helpMeWriteTooltip.tooltip}>
          <Box
            sx={{
              button: {
                marginLeft: 'auto',
              },
            }}
          >
            <GradientButton
              // @ts-ignore
              text="Reanalyse"
              buttonProps={{
                StartIcon: ContentAnalyzerGradient,
              }}
              onClick={handleReanalyze}
              isDisabled={
                !selectedRevision.isReanalyzeEnabled ||
                isRephraseFetching ||
                isAnalysisFetching ||
                helpMeWriteTooltip.isDisabled
              }
            />
          </Box>
        </Tooltip>
      </Flex>
      <Divider color="neutral.EBEFF3" m={1} />
      <FeedbackAnalysis />
    </Flex>
  );
};

export { MeshMavenAnalyze, MeshMavenAnalyzeAndDEI };
